import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, MessageSquare, ShieldCheck, AlertTriangle, Send, User as UserIcon, Store, Shield } from 'lucide-react';
import { getDisputeForTransaction, addDisputeMessage } from '@/lib/api';
import { toast } from 'sonner';
import { fmt2 } from '@/lib/utils';

const DISPUTE_STATUS_LABELS = {
  open: { label: 'Offen', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  under_review: { label: 'In Prüfung', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  resolved_refund_full: { label: 'Vollerstattung', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  resolved_refund_partial: { label: 'Teilerstattung', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  resolved_rejected: { label: 'Abgelehnt', color: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};

const ROLE_ICONS = {
  buyer: UserIcon,
  seller: Store,
  admin: Shield,
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export const DisputeThreadDialog = ({ open, onOpenChange, transactionId, currentUserRole }) => {
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    try {
      const res = await getDisputeForTransaction(transactionId);
      setDispute(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reklamation konnte nicht geladen werden');
      setDispute(null);
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleSend = async () => {
    if (!dispute || message.trim().length < 1) return;
    setSending(true);
    try {
      const res = await addDisputeMessage(dispute.dispute_id, message.trim());
      setDispute(res.data);
      setMessage('');
      toast.success('Nachricht gesendet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nachricht konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  const cfg = dispute ? DISPUTE_STATUS_LABELS[dispute.status] : null;
  const isResolved = dispute?.status?.startsWith('resolved_');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dispute-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Reklamation
            {cfg && <Badge className={cfg.color}>{cfg.label}</Badge>}
          </DialogTitle>
          <DialogDescription>
            {dispute?.transaction?.order_number ? (
              <span className="font-mono text-sm font-semibold text-foreground">{dispute.transaction.order_number}</span>
            ) : 'Reklamationsverlauf'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
          </div>
        ) : !dispute ? (
          <p className="text-center text-muted-foreground py-8">Keine Reklamation gefunden.</p>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 bg-muted/40 rounded-lg p-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Artikel</p>
                <p className="font-medium">{dispute.material?.common_title || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Typ</p>
                <p className="font-medium">{dispute.dispute_type}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Verkäufer</p>
                <p className="font-medium">{dispute.seller_group?.group_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Käufer</p>
                <p className="font-medium">{dispute.buyer_group?.group_name || '—'}</p>
              </div>
              {isResolved && (
                <div className="col-span-2 mt-1 border-t pt-2 border-slate-200 dark:border-slate-700">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Admin-Entscheidung
                  </p>
                  <p className="text-sm font-medium">
                    {dispute.resolution === 'refund_full' && `Vollerstattung: ${fmt2(dispute.refund_amount_eur || 0)} EUR`}
                    {dispute.resolution === 'refund_partial' && `Teilerstattung: ${fmt2(dispute.refund_amount_eur || 0)} EUR`}
                    {dispute.resolution === 'reject' && 'Reklamation abgelehnt'}
                  </p>
                  {dispute.admin_notes && (
                    <p className="text-xs text-muted-foreground italic mt-1">"{dispute.admin_notes}"</p>
                  )}
                </div>
              )}
            </div>

            {/* Evidence */}
            {dispute.evidence_urls?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Belege</p>
                <div className="flex flex-wrap gap-2">
                  {dispute.evidence_urls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                      Beleg #{i + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Message thread */}
            <div className="border rounded-lg divide-y max-h-[40vh] overflow-y-auto" data-testid="dispute-messages-list">
              {(dispute.messages || []).map((msg, idx) => {
                const Icon = ROLE_ICONS[msg.role] || UserIcon;
                const isAdmin = msg.role === 'admin';
                return (
                  <div key={idx} className={`p-3 ${isAdmin ? 'bg-purple-50 dark:bg-purple-900/10' : ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`h-3.5 w-3.5 ${isAdmin ? 'text-purple-600' : 'text-muted-foreground'}`} />
                      <span className="text-xs font-semibold capitalize">{msg.role}</span>
                      <span className="text-xs text-muted-foreground">· {formatDate(msg.at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                );
              })}
            </div>

            {/* Compose */}
            {!isResolved && (
              <div>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={2}
                  placeholder="Nachricht hinzufügen …"
                  data-testid="dispute-message-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={handleSend}
                    disabled={sending || message.trim().length < 1}
                    data-testid="dispute-send-message-btn"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                    Senden
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
